import type { GraphQLFormattedError } from 'graphql';
import { ApolloError } from '@apollo/client/errors';
import { useErrorToast } from '@frameio/shared/resources/shared/hooks';
import { BULK_OPERATION_CHUNK_SIZE } from '../../../bulk-operations/constants';
import {
  useAddBulkAssetOperation,
  useAddMovingAssets,
  useHandleBulkAssetOperationFailed,
  useRemoveMovingAssets,
} from '../../../bulk-operations/contexts';
import { useCreateMoveAssetsOperationMutation } from '../../../bulk-operations/gql/generated/CreateMoveAssetsOperation';
import { useStartMoveAssetsOperationMutation } from '../../../bulk-operations/gql/generated/StartMoveAssetsOperation';
import type { AssetParentFragment } from '../../../shared/gql/generated/AssetParent';
import { INVALID_TARGET_PROJECT_REASON } from '../../constants';
import { getOperationErrorMessage } from '../../utils';
import TEXT from './generated/TEXT/useBulkMoveAssets';

TEXT.DEFINE({
  /** Error shown when a move crosses between regular and managed project storage, in either direction. */
  invalidTargetProject:
    "Moving assets between regular and managed projects isn't supported yet.",
  moveFailed: (assetCount: number): string =>
    `Failed to move ${assetCount} items`,
  moveDenied: "You don't have permission to move these items.",
});

type BulkMoveAssetsProps = {
  assetIds: string[];
  destination: AssetParentFragment;
  movePrivateCommentsToNewWorkspace?: boolean;
  /** whether to disable adding assets from the bulk operation to moving assets context */
  disableAddMovingAssets?: boolean;
  onSuccess?: (destination: AssetParentFragment) => void;
  onError?: (error?: readonly GraphQLFormattedError[]) => void;
};

/**
 * useBulkMoveAssets is used to move large batches of assets. On the BE, bulk moves
 * are processed asynchronously and in chunks, managed by an oban queue. The actual moving of assets
 * is not handled in the lifecycle of the request, but rather via subscription events once the assets are processed.
 */
export const useBulkMoveAssets = () => {
  const addErrorToast = useErrorToast();
  const addBulkAssetOperation = useAddBulkAssetOperation();
  const handlebulkAssetOperationFailed = useHandleBulkAssetOperationFailed();
  const [createMoveAssetsOperation] = useCreateMoveAssetsOperationMutation();
  const [startMoveAssetsOperation] = useStartMoveAssetsOperationMutation();
  const addMovingAssets = useAddMovingAssets();
  const removeMovingAssets = useRemoveMovingAssets();

  return async ({
    assetIds,
    destination,
    movePrivateCommentsToNewWorkspace = false,
    disableAddMovingAssets = false,
    onSuccess,
    onError,
  }: BulkMoveAssetsProps) => {
    const assetCount = assetIds.length;
    let operationId: string;

    const handleResolve = () => {
      onSuccess?.(destination);
    };

    const handleError = (error?: readonly GraphQLFormattedError[]) => {
      handlebulkAssetOperationFailed(operationId, assetCount);
      removeMovingAssets(assetIds);
      onError?.(error);
    };

    /**
     * Initiate Move Asset Operation
     */
    await createMoveAssetsOperation({
      variables: {
        input: {
          assetIds,
          parentId: destination.id,
          movePrivateCommentsToNewWorkspace,
        },
      },
    })
      .then((result) => {
        operationId = result?.data?.createMoveAssetsOperation?.assetOperation
          ?.id as string;

        if (!operationId || typeof operationId !== 'string') {
          throw new Error('Failed to create move assets operation');
        }

        addBulkAssetOperation(operationId, {
          operationName: 'move',
          totalAssetCount: assetCount,
          destination: {
            page: 'folder',
            projectId: destination.project?.id as string,
            folderAssetId: destination.id,
          },
        });

        if (!disableAddMovingAssets) {
          addMovingAssets(assetIds);
        }

        /**
         * Execute Move Asset Operation
         */
        return startMoveAssetsOperation({
          variables: {
            input: {
              assetOperationId: operationId,
              chunkSize: BULK_OPERATION_CHUNK_SIZE,
            },
          },
        }).then(handleResolve, handleError);
      })
      .catch((error: unknown) => {
        const errors = error instanceof ApolloError ? error.graphQLErrors : [];
        const errorMessage = errors.some(
          (graphQLError) =>
            graphQLError.extensions?.reason === INVALID_TARGET_PROJECT_REASON
        )
          ? TEXT.invalidTargetProject
          : getOperationErrorMessage(errors, {
              default: TEXT.moveFailed(assetCount),
              permissionDenied: TEXT.moveDenied,
            });
        addErrorToast({ title: errorMessage });
      });
  };
};
